import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { PageIntro } from '@components/common/PageIntro';
import { Section } from '@components/common/Section';
import { siteConfig } from '@config/site';
import { usePageTitle } from '@hooks/usePageTitle';
import { submitContact, validateContactFields } from '@services/contact/contactSubmission';
const createRequestId = () => {
    if (globalThis.crypto.randomUUID)
        return globalThis.crypto.randomUUID();
    const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
};
export const ContactPage = () => {
    const { t, i18n } = useTranslation();
    const [name, setName] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [problemType, setProblemType] = useState('support');
    const [message, setMessage] = useState('');
    const [website, setWebsite] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [status, setStatus] = useState('idle');
    const requestId = useRef(null);
    const submissionLock = useRef(false);
    const nameRef = useRef(null);
    const emailRef = useRef(null);
    const messageRef = useRef(null);
    const statusRef = useRef(null);
    usePageTitle('contact.title');
    const email = siteConfig.email;
    const fields = { name, email: emailAddress, problemType, message };
    const errors = validateContactFields(fields);
    const hasErrors = Object.values(errors).some(Boolean);
    const changed = () => {
        requestId.current = null;
        if (status !== 'loading')
            setStatus('idle');
    };
    const focusStatus = () => globalThis.setTimeout(() => statusRef.current?.focus(), 0);
    const submit = async (event) => {
        event.preventDefault();
        setSubmitted(true);
        if (submissionLock.current)
            return;
        if (hasErrors) {
            if (errors.name)
                nameRef.current?.focus();
            else if (errors.email)
                emailRef.current?.focus();
            else
                messageRef.current?.focus();
            return;
        }
        if (!requestId.current)
            requestId.current = createRequestId();
        submissionLock.current = true;
        setStatus('loading');
        try {
            await submitContact({
                requestId: requestId.current,
                name: name.trim(),
                email: emailAddress.trim(),
                problemType,
                message: message.trim(),
                locale: i18n.resolvedLanguage ?? i18n.language,
                website
            });
            setStatus('success');
            focusStatus();
        }
        catch {
            setStatus('error');
            focusStatus();
        }
        finally {
            submissionLock.current = false;
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(PageIntro, { title: t('contact.title'), intro: t('contact.intro') }), _jsx(Section, { children: _jsx(Container, { children: _jsxs(ContactLayout, { children: [_jsxs(ContactForm, { onSubmit: submit, noValidate: true, "aria-busy": status === 'loading', children: [_jsx("h2", { children: t('contact.formTitle') }), _jsxs(Field, { "$error": submitted && errors.name, children: [_jsx("label", { htmlFor: "contact-name", children: t('contact.name') }), _jsx("input", { ref: nameRef, id: "contact-name", name: "name", type: "text", autoComplete: "name", maxLength: 120, value: name, onChange: (event) => {
                                                    setName(event.target.value);
                                                    changed();
                                                }, "aria-invalid": submitted && errors.name, "aria-describedby": submitted && errors.name ? 'contact-name-error' : undefined, required: true }), submitted && errors.name && (_jsx(Error, { id: "contact-name-error", children: t('contact.validation.name') }))] }), _jsxs(Field, { "$error": submitted && errors.email, children: [_jsx("label", { htmlFor: "contact-email", children: t('contact.email') }), _jsx("input", { ref: emailRef, id: "contact-email", name: "email", type: "email", autoComplete: "email", maxLength: 254, value: emailAddress, onChange: (event) => {
                                                    setEmailAddress(event.target.value);
                                                    changed();
                                                }, "aria-invalid": submitted && errors.email, "aria-describedby": submitted && errors.email ? 'contact-email-error' : undefined, required: true }), submitted && errors.email && (_jsx(Error, { id: "contact-email-error", children: t('contact.validation.email') }))] }), _jsxs(Field, { children: [_jsx("label", { htmlFor: "contact-problem-type", children: t('contact.problemType') }), _jsxs("select", { id: "contact-problem-type", name: "problemType", value: problemType, onChange: (event) => {
                                                    setProblemType(event.target.value);
                                                    changed();
                                                }, children: [_jsx("option", { value: "support", children: t('contact.problemTypes.support') }), _jsx("option", { value: "consulting", children: t('contact.problemTypes.consulting') }), _jsx("option", { value: "product", children: t('contact.problemTypes.product') })] })] }), _jsxs(Field, { "$error": submitted && errors.message, children: [_jsx("label", { htmlFor: "contact-message", children: t('contact.message') }), _jsx("textarea", { ref: messageRef, id: "contact-message", name: "message", rows: 6, maxLength: 5000, value: message, onChange: (event) => {
                                                    setMessage(event.target.value);
                                                    changed();
                                                }, "aria-invalid": submitted && errors.message, "aria-describedby": submitted && errors.message ? 'contact-message-error' : undefined, required: true }), submitted && errors.message && (_jsx(Error, { id: "contact-message-error", children: t('contact.validation.message') }))] }), _jsxs(Honeypot, { "aria-hidden": "true", children: [_jsx("label", { htmlFor: "contact-website", children: t('contact.honeypot') }), _jsx("input", { id: "contact-website", name: "website", type: "text", tabIndex: -1, autoComplete: "off", value: website, onChange: (event) => setWebsite(event.target.value) })] }), _jsx(Button, { type: "submit", disabled: status === 'loading', children: t(status === 'loading' ? 'contact.submitting' : 'contact.submit') }), status !== 'idle' && (_jsx(FormStatus, { ref: statusRef, tabIndex: -1, role: status === 'error' ? 'alert' : 'status', "$error": status === 'error', children: t(`contact.status.${status}`) }))] }), _jsxs(ContactDetails, { children: [_jsxs(ContactItem, { children: [_jsx("span", { children: t('contact.email') }), siteConfig.email ? (_jsx("a", { href: `mailto:${siteConfig.email}`, children: email })) : (_jsx("strong", { children: email }))] }), siteConfig.phone && (_jsxs(ContactItem, { children: [_jsx("span", { children: t('contact.phone') }), _jsx("strong", { children: siteConfig.phone })] })), siteConfig.supportUrl && (_jsxs(ContactItem, { children: [_jsx("span", { children: t('contact.support') }), _jsx("a", { href: siteConfig.supportUrl, children: siteConfig.supportUrl })] }))] })] }) }) })] }));
};
const ContactLayout = styled.div `
  display: grid;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
    align-items: start;
  }
`;
const ContactForm = styled.form `
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: clamp(1.25rem, 3vw, 2rem);
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.soft};

  h2 {
    margin: 0 0 ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.navy900};
    font-size: ${({ theme }) => theme.typography.sizes.xl};
  }
`;
const Field = styled.div `
  display: grid;
  gap: 0.45rem;

  label {
    color: ${({ theme }) => theme.colors.navy900};
    font-size: ${({ theme }) => theme.typography.sizes.sm};
    font-weight: 800;
  }

  input,
  select,
  textarea {
    width: 100%;
    border: 1px solid
      ${({ theme, $error }) => ($error ? theme.colors.danger : theme.colors.line)};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: 0.85rem 0.95rem;
    color: ${({ theme }) => theme.colors.ink};
    background: ${({ theme }) => theme.colors.surface};
  }

  textarea {
    resize: vertical;
  }
`;
const Error = styled.p `
  margin: 0;
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;
const Honeypot = styled.div `
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;
const FormStatus = styled.p `
  margin: 0;
  color: ${({ theme, $error }) => ($error ? theme.colors.danger : theme.colors.blue600)};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  font-weight: 700;
`;
const ContactDetails = styled.aside `
  display: grid;
  gap: ${({ theme }) => theme.spacing.lg};
`;
const ContactItem = styled.article `
  display: grid;
  gap: ${({ theme }) => theme.spacing.sm};
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  padding-top: ${({ theme }) => theme.spacing.lg};

  span {
    color: ${({ theme }) => theme.colors.blue600};
    font-size: ${({ theme }) => theme.typography.sizes.sm};
    font-weight: 800;
  }

  strong,
  a {
    overflow-wrap: anywhere;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: ${({ theme }) => theme.typography.sizes.lg};
  }
`;
